// @ts-check

import { render } from "preact";
import { html } from "htm/preact";
import { useEffect, useLayoutEffect, useRef } from "preact/hooks";
import {
  batch,
  useComputed,
  useSignal,
  useSignalEffect,
} from "@preact/signals";
import {
  DEFAULT_REDACTION_BRUSH_WIDTH,
  DEFAULT_REDACTION_TOOL,
  REDACTION_BRUSH_WIDTHS,
  appendRedactionPoint,
  appendRedactionStroke,
  createRedactedCanvas,
  createRedactionStroke,
  drawRedactionDraft,
  drawRedactionStrokes,
  normalizeRedactionStroke,
  removeLastRedactionStroke,
} from "./redaction.js";
import { setupServiceWorkerReloadWhenIdle } from "./sw-reload.js";
import {
  applyWatermarkToCanvas,
  computeWatermarkOutputSize,
  normalizeWatermarkText,
} from "./watermark.js";

/**
 * @typedef {{ x: number, y: number }} Point
 * @typedef {"topLeft" | "topRight" | "bottomRight" | "bottomLeft"} CornerId
 * @typedef {{ topLeft: Point, topRight: Point, bottomRight: Point, bottomLeft: Point }} CropCorners
 * @typedef {{ width: number, height: number }} CanvasContentSize
 * @typedef {{ width: number, height: number, pixelRatio: number }} CanvasViewport
 * @typedef {{ x: number, y: number, width: number, height: number, scale: number }} ImagePlacement
 * @typedef {{ width: number, height: number }} OutputSize
 * @typedef {{ top: number, bottom: number }} KeyboardSafeViewport
 * @typedef {"crop" | "watermark" | "processed"} ViewMode
 * @typedef {"neutral" | "danger"} ConfirmIntent
 * @typedef {"brush" | "line"} RedactionTool
 * @typedef {{ title: string, message: string, confirmLabel: string, intent: ConfirmIntent, onConfirm: () => void }} ConfirmDialog
 * @typedef {{ prompt: () => Promise<void> | void, userChoice: Promise<unknown> }} BeforeInstallPromptEvent
 * @typedef {{ cornerId: CornerId, canvasPoint: Point, sourcePoint: Point }} DragPreview
 * @typedef {{ image: HTMLImageElement, dataUrl: string, name: string, width: number, height: number }} SourceDocument
 * @typedef {{ canvas: HTMLCanvasElement, name: string, width: number, height: number }} CroppedDocument
 * @typedef {{ canvas: HTMLCanvasElement, blob: Blob, url: string, name: string, width: number, height: number }} ProcessedDocument
 * @typedef {{ format: "image/png", quality: number, maxDimension: number, operations: unknown[] }} OutputSettings
 * @typedef {{ tool: RedactionTool, width: number, points: Point[] }} RedactionStroke
 * @typedef {import("@preact/signals").Signal<SourceDocument | null>} SourceDocumentSignal
 * @typedef {import("@preact/signals").Signal<CropCorners | null>} CropCornersSignal
 * @typedef {import("@preact/signals").Signal<CroppedDocument | null>} CroppedDocumentSignal
 * @typedef {import("@preact/signals").Signal<ProcessedDocument | null>} ProcessedDocumentSignal
 * @typedef {import("@preact/signals").Signal<CornerId | null>} ActiveCornerSignal
 * @typedef {import("@preact/signals").Signal<DragPreview | null>} DragPreviewSignal
 * @typedef {import("@preact/signals").Signal<CanvasViewport>} CanvasViewportSignal
 * @typedef {import("@preact/signals").Signal<ViewMode>} ViewModeSignal
 * @typedef {import("@preact/signals").Signal<RedactionTool>} RedactionToolSignal
 * @typedef {import("@preact/signals").Signal<number>} BrushWidthSignal
 * @typedef {import("@preact/signals").Signal<RedactionStroke[]>} RedactionStrokesSignal
 * @typedef {import("@preact/signals").Signal<RedactionStroke | null>} RedactionDraftSignal
 */

const CONFIG = {
  fallbackCanvasWidth: 900,
  fallbackCanvasHeight: 1200,
  outputMaxDimension: 2400,
  cropInsetRatio: 0.08,
  cornerHitRadius: 48,
  cornerHandleRadius: 20,
  dragLensRadius: 58,
  dragLensGap: 24,
  dragLensZoom: 2.7,
  canvasPadding: 24,
  keyboardSafeGap: 12,
  minPolygonArea: 400,
  // Sample output pixels from their center when inverse-warping into source space.
  warpPixelCenterOffset: 0.5,
  // RGBA channel value used for source samples outside the selected quadrilateral.
  warpBackgroundValue: 255,
  // Browser ImageData stores pixels as red, green, blue, alpha channels.
  imageDataChannelCount: 4,
  // Bilinear interpolation samples the current source pixel and its next neighbor.
  bilinearNeighborOffset: 1,
};

/** @type {CornerId[]} */
const cornerIds = ["topLeft", "topRight", "bottomRight", "bottomLeft"];

const cornerLabels = {
  topLeft: "Top left",
  topRight: "Top right",
  bottomRight: "Bottom right",
  bottomLeft: "Bottom left",
};

const serviceWorkerUpdateState = {
  activePointerCount: 0,
  hasProjectContent: false,
  isProcessing: false,
};
const appViewportState = {
  stableHeight: 0,
};
const APP_SHELL_ID = "doc-proc-app-shell";

function App() {
  const documentCanvasRef =
    /** @type {import("preact").RefObject<HTMLCanvasElement | null>} */ (useRef(null));
  const fileInputRef =
    /** @type {import("preact").RefObject<HTMLInputElement | null>} */ (useRef(null));

  const sourceDocument = useSignal(/** @type {SourceDocument | null} */ (null));
  const cropCorners = useSignal(/** @type {CropCorners | null} */ (null));
  const croppedDocument = useSignal(
    /** @type {CroppedDocument | null} */ (null),
  );
  const processedDocument = useSignal(
    /** @type {ProcessedDocument | null} */ (null),
  );
  const canvasViewport = useSignal(createDefaultCanvasViewport());
  const viewMode = useSignal(/** @type {ViewMode} */ ("crop"));
  const activeCornerId = useSignal(/** @type {CornerId | null} */ (null));
  const dragPreview = useSignal(/** @type {DragPreview | null} */ (null));
  const redactionTool = useSignal(
    /** @type {RedactionTool} */ (DEFAULT_REDACTION_TOOL),
  );
  const brushWidth = useSignal(DEFAULT_REDACTION_BRUSH_WIDTH);
  const redactionStrokes = useSignal(/** @type {RedactionStroke[]} */ ([]));
  const redactionDraft = useSignal(/** @type {RedactionStroke | null} */ (null));
  const watermarkText = useSignal("CONTROLLED COPY");
  const installPrompt = useSignal(
    /** @type {BeforeInstallPromptEvent | null} */ (null),
  );
  const confirmDialog = useSignal(/** @type {ConfirmDialog | null} */ (null));
  const statusMessage = useSignal("No image selected");
  const isProcessing = useSignal(false);
  const outputSettings = useSignal(createDefaultOutputSettings());

  const outputSize = useComputed(function computeOutputSize() {
    let size = null;
    const corners = cropCorners.value;
    if (corners !== null) {
      size = calculateOutputSize(corners, outputSettings.value.maxDimension);
    }

    return size;
  });

  const watermarkOutputSize = useComputed(function computeWatermarkSize() {
    let size = null;
    const cropped = croppedDocument.value;
    if (cropped !== null) {
      const watermarkSize = computeWatermarkOutputSize(
        cropped.width,
        cropped.height,
      );
      size = {
        width: watermarkSize.outputWidth,
        height: watermarkSize.outputHeight,
      };
    }

    return size;
  });

  const canProcess = useComputed(function computeCanProcess() {
    let result = false;
    if (isProcessing.value) {
      return result;
    }

    if (viewMode.value === "crop") {
      const hasSource = sourceDocument.value !== null;
      const hasCorners = cropCorners.value !== null;
      if (hasSource && hasCorners) {
        result = true;
      }

      return result;
    }

    if (viewMode.value === "watermark" && croppedDocument.value !== null) {
      result = true;
    }

    return result;
  });

  useCanvasResize(documentCanvasRef, canvasViewport);

  useCornerInteraction(
    documentCanvasRef,
    canvasViewport,
    sourceDocument,
    cropCorners,
    activeCornerId,
    dragPreview,
    croppedDocument,
    redactionStrokes,
    redactionDraft,
    processedDocument,
    viewMode,
  );

  useRedactionInteraction(
    documentCanvasRef,
    canvasViewport,
    croppedDocument,
    viewMode,
    redactionTool,
    brushWidth,
    redactionStrokes,
    redactionDraft,
    processedDocument,
  );

  useSignalEffect(function drawDocument() {
    const canvas = documentCanvasRef.current;
    if (!(canvas instanceof HTMLCanvasElement)) {
      return;
    }

    drawDocumentCanvas(
      canvas,
      canvasViewport.value,
      viewMode.value,
      sourceDocument.value,
      cropCorners.value,
      activeCornerId.value,
      dragPreview.value,
      croppedDocument.value,
      redactionStrokes.value,
      redactionDraft.value,
      processedDocument.value,
    );
  });

  useSignalEffect(function syncServiceWorkerIdleState() {
    const hasSource = sourceDocument.value !== null;
    const hasProcessedDocument = processedDocument.value !== null;
    setHasProjectContent(hasSource || hasProcessedDocument);
    setProcessingState(isProcessing.value);
  });

  useEffect(
    function setupInstallPrompt() {
      /** @param {Event} event */
      function handleBeforeInstallPrompt(event) {
        event.preventDefault();
        installPrompt.value = /** @type {BeforeInstallPromptEvent} */ (
          /** @type {unknown} */ (event)
        );
      }

      function handleAppInstalled() {
        installPrompt.value = null;
      }

      window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.addEventListener("appinstalled", handleAppInstalled);

      return function cleanupInstallPrompt() {
        window.removeEventListener(
          "beforeinstallprompt",
          handleBeforeInstallPrompt,
        );
        window.removeEventListener("appinstalled", handleAppInstalled);
      };
    },
    [installPrompt],
  );

  useEffect(function setupPasteImport() {
    /** @param {ClipboardEvent} event */
    async function handlePaste(event) {
      const file = getImageFileFromClipboard(event.clipboardData);
      if (file === null) {
        return;
      }

      event.preventDefault();
      await loadImageFile(file);
    }

    document.addEventListener("paste", handlePaste);
    return function cleanupPasteImport() {
      document.removeEventListener("paste", handlePaste);
    };
  }, []);

  useEffect(
    function cleanupProcessedDocumentOnUnmount() {
      return function cleanupProcessedDocument() {
        clearProcessedDocument(processedDocument);
      };
    },
    [processedDocument],
  );

  function openFilePicker() {
    const input = fileInputRef.current;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    input.click();
  }

  /** @param {Event} event */
  async function handleFileChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    const file = getImageFileFromList(input.files);
    input.value = "";
    if (file === null) {
      statusMessage.value = "Select an image file";
      return;
    }

    await loadImageFile(file);
  }

  /** @param {File} file */
  async function loadImageFile(file) {
    if (isProcessing.peek()) {
      return;
    }

    isProcessing.value = true;
    statusMessage.value = "Loading image";

    try {
      const source = await createSourceDocument(file);
      const initialCorners = createInitialCorners(source.width, source.height);
      clearProcessedDocument(processedDocument);
      batch(function updateSourceDocument() {
        sourceDocument.value = source;
        cropCorners.value = initialCorners;
        croppedDocument.value = null;
        redactionStrokes.value = [];
        redactionDraft.value = null;
        watermarkText.value = createDefaultWatermarkCode(source.name);
        viewMode.value = "crop";
        activeCornerId.value = null;
        statusMessage.value = "Image ready";
      });
    } catch (error) {
      statusMessage.value = getErrorMessage(error);
    } finally {
      isProcessing.value = false;
    }
  }

  function resetCorners() {
    const source = sourceDocument.peek();
    if (source === null) {
      return;
    }

    const initialCorners = createInitialCorners(source.width, source.height);
    clearProcessedDocument(processedDocument);
    batch(function resetCropState() {
      cropCorners.value = initialCorners;
      croppedDocument.value = null;
      redactionStrokes.value = [];
      redactionDraft.value = null;
      activeCornerId.value = null;
      viewMode.value = "crop";
      statusMessage.value = "Corners reset";
    });
  }

  function clearProject() {
    clearProcessedDocument(processedDocument);
    batch(function clearCurrentProject() {
      sourceDocument.value = null;
      cropCorners.value = null;
      croppedDocument.value = null;
      redactionStrokes.value = [];
      redactionDraft.value = null;
      activeCornerId.value = null;
      viewMode.value = "crop";
      statusMessage.value = "No image selected";
    });
  }

  function confirmResetCorners() {
    const source = sourceDocument.peek();
    if (source === null) {
      return;
    }

    showConfirmation(confirmDialog, {
      title: "Reset crop?",
      message: "Reset the crop corners to the image bounds.",
      confirmLabel: "Reset",
      intent: "neutral",
      onConfirm: resetCorners,
    });
  }

  function confirmClearProject() {
    const hasSource = sourceDocument.peek() !== null;
    const hasOutput = processedDocument.peek() !== null;
    if (!hasSource && !hasOutput) {
      return;
    }

    showConfirmation(confirmDialog, {
      title: "Clear document?",
      message: "Remove the current image and processed scan.",
      confirmLabel: "Clear",
      intent: "danger",
      onConfirm: clearProject,
    });
  }

  async function processCurrentStep() {
    if (viewMode.peek() === "crop") {
      await processCropStep();
      return;
    }

    if (viewMode.peek() === "watermark") {
      await processWatermarkStep();
    }
  }

  async function saveCroppedImage() {
    await processCropStep(true);
  }

  async function skipWatermark() {
    await processWatermarkStep(false);
  }

  /** @param {boolean} [saveDirectly] */
  async function processCropStep(saveDirectly = false) {
    if (isProcessing.peek()) {
      return;
    }

    const source = sourceDocument.peek();
    const corners = cropCorners.peek();
    if (source === null || corners === null) {
      return;
    }

    if (!isUsableQuadrilateral(corners)) {
      statusMessage.value = "Move corners into a four-sided crop";
      return;
    }

    isProcessing.value = true;
    statusMessage.value = "Cropping image";
    await waitForNextFrame();

    try {
      const settings = outputSettings.peek();
      const cropped = createCroppedDocument(
        source,
        corners,
        settings,
      );
      const processed = saveDirectly
        ? await createProcessedDocument(cropped, [], null)
        : null;
      batch(function showCropResult() {
        croppedDocument.value = cropped;
        redactionStrokes.value = [];
        redactionDraft.value = null;
        clearProcessedDocument(processedDocument);
        viewMode.value = processed === null ? "watermark" : "processed";
        processedDocument.value = processed;
        statusMessage.value = processed === null
          ? "Add watermark or redact sensitive information"
          : "Cropped image ready";
      });
      if (processed !== null) {
        downloadBlob(processed.blob, processed.name);
      }
    } catch (error) {
      statusMessage.value = getErrorMessage(error);
    } finally {
      isProcessing.value = false;
    }
  }

  /** @param {boolean} [includeWatermark] */
  async function processWatermarkStep(includeWatermark = true) {
    if (isProcessing.peek()) {
      return;
    }

    const cropped = croppedDocument.peek();
    if (cropped === null) {
      return;
    }

    isProcessing.value = true;
    statusMessage.value = includeWatermark ? "Applying watermark…" : "Preparing image…";
    await waitForNextFrame();

    try {
      const text = includeWatermark ? watermarkText.peek() : null;
      const strokes = redactionStrokes.peek();
      const processed = await createProcessedDocument(cropped, strokes, text);
      replaceProcessedDocument(processedDocument, processed);
      viewMode.value = "processed";
      statusMessage.value = "Scan ready";
    } catch (error) {
      statusMessage.value = getErrorMessage(error);
    } finally {
      isProcessing.value = false;
    }
  }

  function editCrop() {
    const source = sourceDocument.peek();
    if (source === null) {
      return;
    }

    viewMode.value = "crop";
    statusMessage.value = "Editing crop";
  }

  function editWatermark() {
    const cropped = croppedDocument.peek();
    if (cropped === null) {
      editCrop();
      return;
    }

    viewMode.value = "watermark";
    statusMessage.value = "Editing watermark";
  }

  function undoRedaction() {
    redactionStrokes.value = removeLastRedactionStroke(redactionStrokes.peek());
    redactionDraft.value = null;
    clearProcessedDocument(processedDocument);
    statusMessage.value = "Redaction undone";
  }

  function clearRedactions() {
    redactionStrokes.value = [];
    redactionDraft.value = null;
    clearProcessedDocument(processedDocument);
    statusMessage.value = "Redactions cleared";
  }

  /** @param {RedactionTool} tool */
  function setRedactionTool(tool) {
    redactionTool.value = tool;
  }

  /** @param {number} width */
  function setBrushWidth(width) {
    brushWidth.value = width;
  }

  /** @param {Event} event */
  function handleWatermarkTextInput(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    watermarkText.value = input.value;
    clearProcessedDocument(processedDocument);
  }

  /** @param {Event} event */
  function handleWatermarkTextFocus(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    requestKeyboardSafeScroll(input);
  }

  function downloadScan() {
    const processed = processedDocument.peek();
    if (processed === null) {
      return;
    }

    downloadBlob(processed.blob, processed.name);
  }

  async function copyScan() {
    const processed = processedDocument.peek();
    if (processed === null) {
      return;
    }

    const clipboardItemSupported = typeof ClipboardItem !== "undefined";
    if (!navigator.clipboard || !clipboardItemSupported) {
      statusMessage.value = "Clipboard image copy is not available";
      return;
    }

    try {
      const item = new ClipboardItem({ [processed.blob.type]: processed.blob });
      await navigator.clipboard.write([item]);
      statusMessage.value = "Copied";
    } catch (error) {
      statusMessage.value = getErrorMessage(error);
    }
  }

  async function shareScan() {
    const processed = processedDocument.peek();
    if (processed === null) {
      return;
    }

    const file = new File([processed.blob], processed.name, {
      type: processed.blob.type,
    });
    const shareData = {
      files: [file],
      title: "Doc Proc scan",
    };
    const canShare = navigator.canShare ? navigator.canShare(shareData) : false;
    if (!navigator.share || !canShare) {
      downloadScan();
      return;
    }

    try {
      await navigator.share(shareData);
      statusMessage.value = "Shared";
    } catch (error) {
      statusMessage.value = getErrorMessage(error);
    }
  }

  async function installApp() {
    const promptEvent = installPrompt.peek();
    if (promptEvent === null) {
      return;
    }

    installPrompt.value = null;
    try {
      await promptEvent.prompt();
      await promptEvent.userChoice;
    } catch (error) {
      statusMessage.value = getErrorMessage(error);
    }
  }

  const source = sourceDocument.value;
  const cropped = croppedDocument.value;
  const processed = processedDocument.value;
  const hasSource = source !== null;
  const hasProjectContent = hasSource || cropped !== null || processed !== null;
  const canvasMeta = getCanvasMeta(
    viewMode.value,
    source,
    cropped,
    processed,
    outputSize.value,
    watermarkOutputSize.value,
  );
  const imageInputId = "doc-proc-image-picker";

  return html`
    <main id=${APP_SHELL_ID} class="app-shell">
      <div class="workspace">
        <div class="editor">
          <header class="app-header">
            <div class="document-status">
              <h1>Doc Proc</h1>
              <p role="status">${statusMessage.value}</p>
            </div>
            <div class="button-row">
              ${installPrompt.value !== null && html`<button onClick=${installApp}>Install</button>`}
              <input id=${imageInputId} ref=${fileInputRef} type="file" accept="image/*"
                aria-label="Select image" disabled=${isProcessing.value} class="image-picker" onChange=${handleFileChange} />
              <button disabled=${isProcessing.value} onClick=${openFilePicker}>Select image</button>
            </div>
          </header>
          <section class="canvas-area" inert=${isProcessing.value}>
            <${CanvasPanel} canvasRef=${documentCanvasRef} meta=${canvasMeta}
              viewMode=${viewMode.value} hasProjectContent=${hasProjectContent}
              onRequestImage=${openFilePicker} />
          </section>
          ${viewMode.value === "watermark" && html`<${WatermarkPanel}
            watermarkText=${watermarkText.value} tool=${redactionTool.value}
            brushWidth=${brushWidth.value} strokeCount=${redactionStrokes.value.length}
            onWatermarkTextInput=${handleWatermarkTextInput} onWatermarkTextFocus=${handleWatermarkTextFocus}
            onToolChange=${setRedactionTool} onBrushWidthChange=${setBrushWidth}
            onUndo=${undoRedaction} onClear=${clearRedactions} disabled=${isProcessing.value} />`}
          <${ControlPanel} canProcess=${canProcess.value} hasSource=${hasSource}
            hasCropped=${cropped !== null} hasOutput=${processed !== null}
            isProcessing=${isProcessing.value} viewMode=${viewMode.value}
            onProcess=${processCurrentStep} onSaveCrop=${saveCroppedImage} onSkipWatermark=${skipWatermark}
            onReset=${confirmResetCorners} onClear=${confirmClearProject}
            onEditCrop=${editCrop} onEditWatermark=${editWatermark}
            onDownload=${downloadScan} onCopy=${copyScan} onShare=${shareScan} />
        </div>
        <${DetailsPanel} source=${source} corners=${cropCorners.value}
          plannedOutputSize=${outputSize.value} watermarkOutputSize=${watermarkOutputSize.value}
          cropped=${cropped} processed=${processed} viewport=${canvasViewport.value}
          viewMode=${viewMode.value} redactionCount=${redactionStrokes.value.length} />
      </div>
      ${confirmDialog.value !== null && html`<${ConfirmationDialog}
        dialog=${confirmDialog.value} onCancel=${function cancelConfirmation() {
          confirmDialog.value = null;
        }} />`}
    </main>
  `;
}

/**
 * @param {{ meta: string, canvasRef: import("preact").RefObject<HTMLCanvasElement | null>,
 * viewMode: ViewMode, hasProjectContent: boolean, onRequestImage: () => void }} props
 */
function CanvasPanel(props) {
  const interactionClass = props.viewMode === "processed" || !props.hasProjectContent
    ? "canvas-scroll-control" : "canvas-crop-control";
  return html`
    <section class="canvas-panel">
      <canvas ref=${props.canvasRef} class=${`canvas-touch-control ${interactionClass}`}
        aria-label="Document image" />
      ${!props.hasProjectContent && html`<button class="canvas-import" onClick=${props.onRequestImage}>
        Select an image or paste a photo
      </button>`}
      <div class="canvas-meta">${props.meta}</div>
    </section>
  `;
}

/**
 * @param {{ watermarkText: string, tool: RedactionTool, brushWidth: number, strokeCount: number,
 * onWatermarkTextInput: (event: Event) => void, onWatermarkTextFocus: (event: Event) => void,
 * onToolChange: (tool: RedactionTool) => void, onBrushWidthChange: (width: number) => void,
 * onUndo: () => void, onClear: () => void, disabled: boolean }} props
 */
function WatermarkPanel(props) {
  const widthButtons = [];
  for (let i = 0, total = REDACTION_BRUSH_WIDTHS.length; i < total; i++) {
    const width = REDACTION_BRUSH_WIDTHS[i];
    widthButtons.push(html`<button key=${width} type="button" aria-pressed=${width === props.brushWidth}
      onClick=${function selectWidth() {
        props.onBrushWidthChange(width);
      }}>${width}</button>`);
  }
  return html`
    <fieldset class="watermark-panel" disabled=${props.disabled}>
      <legend>Watermark & redaction</legend>
      <label class="watermark-field">Watermark code
        <input type="text" name="watermark" autocomplete="off" value=${props.watermarkText}
          onInput=${props.onWatermarkTextInput} onFocus=${props.onWatermarkTextFocus}
          class="keyboard-safe-control" />
      </label>
      <div class="redaction-controls">
        <div class="field-group"><span>Tool</span><div class="segments" role="group" aria-label="Redaction tool">
          <button aria-pressed=${props.tool === "brush"}
            onClick=${function selectBrush() {
              props.onToolChange("brush");
            }}>Brush</button>
          <button aria-pressed=${props.tool === "line"}
            onClick=${function selectLine() {
              props.onToolChange("line");
            }}>Line</button>
        </div></div>
        <div class="field-group"><span>Width</span><div class="segments" role="group" aria-label="Brush width">
          ${widthButtons}
        </div></div>
        <div class="button-row">
          <button disabled=${props.strokeCount === 0} onClick=${props.onUndo}>Undo</button>
          <button class="danger" disabled=${props.strokeCount === 0} onClick=${props.onClear}>Clear redactions</button>
        </div>
      </div>
    </fieldset>
  `;
}

/**
 * @param {{ canProcess: boolean, hasSource: boolean, hasCropped: boolean, hasOutput: boolean,
 * isProcessing: boolean, viewMode: ViewMode, onProcess: () => void, onSaveCrop: () => void,
 * onSkipWatermark: () => void, onReset: () => void, onClear: () => void,
 * onEditCrop: () => void, onEditWatermark: () => void, onDownload: () => void,
 * onCopy: () => void, onShare: () => void }} props
 */
function ControlPanel(props) {
  const isCrop = props.viewMode === "crop";
  const isWatermark = props.viewMode === "watermark";
  const isOutput = props.viewMode === "processed";
  return html`
    <fieldset class="controls" disabled=${props.isProcessing} aria-label="Document actions">
      <div class="primary-actions">
        ${isCrop && html`
          <button class="primary" disabled=${!props.canProcess} onClick=${props.onSaveCrop}>
            ${props.isProcessing ? "Cropping…" : "Save cropped image"}
          </button>
          <button disabled=${!props.canProcess} onClick=${props.onProcess}>Watermark & redact</button>`}
        ${isWatermark && html`
          <button class="primary" disabled=${!props.canProcess} onClick=${props.onProcess}>
            ${props.isProcessing ? "Processing…" : "Apply watermark"}
          </button>
          <button disabled=${!props.canProcess} onClick=${props.onSkipWatermark}>Skip watermark</button>`}
        ${isOutput && html`
          <button class="primary" disabled=${!props.hasOutput} onClick=${props.onDownload}>Save image</button>
          <button disabled=${!props.hasOutput} onClick=${props.onCopy}>Copy</button>
          <button disabled=${!props.hasOutput} onClick=${props.onShare}>Share</button>`}
      </div>
      <div class="secondary-actions">
        ${!isCrop && html`<button onClick=${props.onEditCrop}>Edit crop</button>`}
        ${isOutput && props.hasCropped && html`<button onClick=${props.onEditWatermark}>Watermark & redact</button>`}
        ${isCrop && html`<button disabled=${!props.hasSource} onClick=${props.onReset}>Reset corners</button>`}
        <button class="danger" disabled=${!props.hasSource && !props.hasOutput} onClick=${props.onClear}>Clear document</button>
      </div>
    </fieldset>
  `;
}

/**
 * @param {{ source: SourceDocument | null, corners: CropCorners | null,
 * plannedOutputSize: OutputSize | null, watermarkOutputSize: OutputSize | null,
 * cropped: CroppedDocument | null, processed: ProcessedDocument | null,
 * viewport: CanvasViewport, viewMode: ViewMode, redactionCount: number }} props
 */
function DetailsPanel(props) {
  return html`
    <details class="details-panel">
      <summary>Document details</summary>
      <dl>
        <${DetailsRow} label="View" value=${getViewModeLabel(props.viewMode)} />
        <${DetailsRow} label="Image" value=${getSourceDetailValue(props.source)} />
        <${DetailsRow} label="Crop" value=${getOutputSizeDetailValue(props.plannedOutputSize)} />
        <${DetailsRow} label="Watermark" value=${getOutputSizeDetailValue(props.watermarkOutputSize)} />
        <${DetailsRow} label="Cropped" value=${getCroppedDetailValue(props.cropped)} />
        <${DetailsRow} label="Processed" value=${getProcessedDetailValue(props.processed)} />
        <${DetailsRow} label="Redactions" value=${String(props.redactionCount)} />
        <${DetailsRow} label="Canvas" value=${getViewportDetailValue(props.viewport)} />
      </dl>
      <h3>Corners</h3>
      <dl>${getCornerDetailRows(props.corners)}</dl>
    </details>
  `;
}

/** @param {{ label: string, value: string }} props */
function DetailsRow(props) {
  return html`<div class="detail-row"><dt>${props.label}</dt><dd>${props.value}</dd></div>`;
}

/** @param {{ dialog: ConfirmDialog, onCancel: () => void }} props */
function ConfirmationDialog(props) {
  const dialogRef = /** @type {import("preact").RefObject<HTMLDialogElement | null>} */ (useRef(null));
  useLayoutEffect(function openDialog() {
    const dialog = dialogRef.current;
    dialog?.showModal();
    return function closeDialog() {
      dialog?.close();
    };
  }, []);

  function confirm() {
    props.onCancel();
    props.dialog.onConfirm();
  }

  return html`
    <dialog ref=${dialogRef} class="confirmation-dialog" aria-labelledby="confirm-title" onCancel=${props.onCancel}>
      <h2 id="confirm-title">${props.dialog.title}</h2>
      <p>${props.dialog.message}</p>
      <div class="button-row">
        <button autofocus onClick=${props.onCancel}>Cancel</button>
        <button class=${props.dialog.intent === "danger" ? "danger" : "primary"} onClick=${confirm}>
          ${props.dialog.confirmLabel}
        </button>
      </div>
    </dialog>
  `;
}

/**
 * @param {import("preact").RefObject<HTMLCanvasElement | null>} canvasRef
 * @param {CanvasViewportSignal} canvasViewport
 * @param {SourceDocumentSignal} sourceDocument
 * @param {CropCornersSignal} cropCorners
 * @param {ActiveCornerSignal} activeCornerId
 * @param {DragPreviewSignal} dragPreview
 * @param {CroppedDocumentSignal} croppedDocument
 * @param {RedactionStrokesSignal} redactionStrokes
 * @param {RedactionDraftSignal} redactionDraft
 * @param {ProcessedDocumentSignal} processedDocument
 * @param {ViewModeSignal} viewMode
 * @returns {void}
 */
function useCornerInteraction(
  canvasRef,
  canvasViewport,
  sourceDocument,
  cropCorners,
  activeCornerId,
  dragPreview,
  croppedDocument,
  redactionStrokes,
  redactionDraft,
  processedDocument,
  viewMode,
) {
  const pointerStateRef =
    /** @type {{ current: { pointerId: number | null, cornerId: CornerId | null } }} */ (
      useRef({
        pointerId: /** @type {number | null} */ (null),
        cornerId: /** @type {CornerId | null} */ (null),
      })
    );

  useEffect(
    function setupCornerInteraction() {
      const canvas = canvasRef.current;
      if (!(canvas instanceof HTMLCanvasElement)) {
        return undefined;
      }

      const targetCanvas = canvas;

      /** @param {PointerEvent} event */
      function handlePointerDown(event) {
        const source = sourceDocument.peek();
        const corners = cropCorners.peek();
        const isEditing = viewMode.peek() === "crop";
        if (source === null || corners === null || !isEditing) {
          return;
        }

        event.preventDefault();
        const viewport = canvasViewport.peek();
        const cornerId = findHitCorner(
          event,
          targetCanvas,
          source,
          corners,
          viewport,
        );
        if (cornerId === null) {
          return;
        }

        targetCanvas.setPointerCapture(event.pointerId);
        pointerStateRef.current.pointerId = event.pointerId;
        pointerStateRef.current.cornerId = cornerId;
        activeCornerId.value = cornerId;
        setActivePointerCount(1);
        updateCornerFromPointer(
          event,
          targetCanvas,
          canvasViewport,
          sourceDocument,
          cropCorners,
          dragPreview,
          croppedDocument,
          redactionStrokes,
          redactionDraft,
          processedDocument,
          cornerId,
        );
      }

      /** @param {PointerEvent} event */
      function handlePointerMove(event) {
        const state = pointerStateRef.current;
        if (state.pointerId !== event.pointerId || state.cornerId === null) {
          return;
        }

        event.preventDefault();
        updateCornerFromPointer(
          event,
          targetCanvas,
          canvasViewport,
          sourceDocument,
          cropCorners,
          dragPreview,
          croppedDocument,
          redactionStrokes,
          redactionDraft,
          processedDocument,
          state.cornerId,
        );
      }

      /** @param {PointerEvent} event */
      function handlePointerEnd(event) {
        const state = pointerStateRef.current;
        if (state.pointerId !== event.pointerId) {
          return;
        }

        if (targetCanvas.hasPointerCapture(event.pointerId)) {
          targetCanvas.releasePointerCapture(event.pointerId);
        }

        state.pointerId = null;
        state.cornerId = null;
        batch(function stopCornerDrag() {
          activeCornerId.value = null;
          dragPreview.value = null;
        });
        setActivePointerCount(0);
      }

      targetCanvas.addEventListener("pointerdown", handlePointerDown);
      targetCanvas.addEventListener("pointermove", handlePointerMove);
      targetCanvas.addEventListener("pointerup", handlePointerEnd);
      targetCanvas.addEventListener("pointercancel", handlePointerEnd);

      return function cleanupCornerInteraction() {
        targetCanvas.removeEventListener("pointerdown", handlePointerDown);
        targetCanvas.removeEventListener("pointermove", handlePointerMove);
        targetCanvas.removeEventListener("pointerup", handlePointerEnd);
        targetCanvas.removeEventListener("pointercancel", handlePointerEnd);
      };
    },
    [
      canvasRef,
      canvasViewport,
      sourceDocument,
      cropCorners,
      activeCornerId,
      dragPreview,
      croppedDocument,
      redactionStrokes,
      redactionDraft,
      processedDocument,
      viewMode,
    ],
  );
}

/**
 * @param {import("preact").RefObject<HTMLCanvasElement | null>} canvasRef
 * @param {CanvasViewportSignal} canvasViewport
 * @param {CroppedDocumentSignal} croppedDocument
 * @param {ViewModeSignal} viewMode
 * @param {RedactionToolSignal} redactionTool
 * @param {BrushWidthSignal} brushWidth
 * @param {RedactionStrokesSignal} redactionStrokes
 * @param {RedactionDraftSignal} redactionDraft
 * @param {ProcessedDocumentSignal} processedDocument
 * @returns {void}
 */
function useRedactionInteraction(
  canvasRef,
  canvasViewport,
  croppedDocument,
  viewMode,
  redactionTool,
  brushWidth,
  redactionStrokes,
  redactionDraft,
  processedDocument,
) {
  const pointerStateRef =
    /** @type {{ current: { pointerId: number | null } }} */ (
      useRef({
        pointerId: /** @type {number | null} */ (null),
      })
    );

  useEffect(
    function setupRedactionInteraction() {
      const canvas = canvasRef.current;
      if (!(canvas instanceof HTMLCanvasElement)) {
        return undefined;
      }

      const targetCanvas = canvas;

      /** @param {PointerEvent} event */
      function handlePointerDown(event) {
        const cropped = croppedDocument.peek();
        const isWatermarkStep = viewMode.peek() === "watermark";
        if (cropped === null || !isWatermarkStep) {
          return;
        }

        const point = pointerToCroppedPoint(
          event,
          targetCanvas,
          canvasViewport.peek(),
          cropped,
          false,
        );
        if (point === null) {
          return;
        }

        event.preventDefault();
        targetCanvas.setPointerCapture(event.pointerId);
        pointerStateRef.current.pointerId = event.pointerId;
        const stroke = createRedactionStroke(
          redactionTool.peek(),
          brushWidth.peek(),
          point,
        );
        redactionDraft.value = stroke;
        setActivePointerCount(1);
      }

      /** @param {PointerEvent} event */
      function handlePointerMove(event) {
        const state = pointerStateRef.current;
        if (state.pointerId !== event.pointerId) {
          return;
        }

        const cropped = croppedDocument.peek();
        const draft = redactionDraft.peek();
        if (cropped === null || draft === null) {
          return;
        }

        const point = pointerToCroppedPoint(
          event,
          targetCanvas,
          canvasViewport.peek(),
          cropped,
          true,
        );
        if (point === null) {
          return;
        }

        event.preventDefault();
        redactionDraft.value = appendRedactionPoint(draft, point);
      }

      /** @param {PointerEvent} event */
      function handlePointerEnd(event) {
        const state = pointerStateRef.current;
        if (state.pointerId !== event.pointerId) {
          return;
        }

        if (targetCanvas.hasPointerCapture(event.pointerId)) {
          targetCanvas.releasePointerCapture(event.pointerId);
        }

        const draft = redactionDraft.peek();
        state.pointerId = null;
        if (draft !== null) {
          const normalized = normalizeRedactionStroke(draft);
          redactionStrokes.value = appendRedactionStroke(
            redactionStrokes.peek(),
            normalized,
          );
          clearProcessedDocument(processedDocument);
        }

        redactionDraft.value = null;
        setActivePointerCount(0);
      }

      targetCanvas.addEventListener("pointerdown", handlePointerDown);
      targetCanvas.addEventListener("pointermove", handlePointerMove);
      targetCanvas.addEventListener("pointerup", handlePointerEnd);
      targetCanvas.addEventListener("pointercancel", handlePointerEnd);

      return function cleanupRedactionInteraction() {
        targetCanvas.removeEventListener("pointerdown", handlePointerDown);
        targetCanvas.removeEventListener("pointermove", handlePointerMove);
        targetCanvas.removeEventListener("pointerup", handlePointerEnd);
        targetCanvas.removeEventListener("pointercancel", handlePointerEnd);
      };
    },
    [
      canvasRef,
      canvasViewport,
      croppedDocument,
      viewMode,
      redactionTool,
      brushWidth,
      redactionStrokes,
      redactionDraft,
      processedDocument,
    ],
  );
}

/**
 * @returns {OutputSettings}
 */
function createDefaultOutputSettings() {
  return {
    format: "image/png",
    quality: 0.96,
    maxDimension: CONFIG.outputMaxDimension,
    operations: [],
  };
}

/**
 * @returns {CanvasViewport}
 */
function createDefaultCanvasViewport() {
  return {
    width: CONFIG.fallbackCanvasWidth,
    height: CONFIG.fallbackCanvasHeight,
    pixelRatio: getCanvasPixelRatio(),
  };
}

/**
 * @param {import("preact").RefObject<HTMLCanvasElement | null>} canvasRef
 * @param {CanvasViewportSignal} canvasViewport
 * @returns {void}
 */
function useCanvasResize(canvasRef, canvasViewport) {
  useEffect(
    function setupCanvasResize() {
      const canvas = canvasRef.current;
      if (!(canvas instanceof HTMLCanvasElement)) {
        return undefined;
      }

      const targetCanvas = canvas;

      function updateCanvasViewport() {
        const rect = targetCanvas.getBoundingClientRect();
        const width = Math.max(1, Math.round(rect.width));
        const height = Math.max(1, Math.round(rect.height));
        const pixelRatio = getCanvasPixelRatio();
        const current = canvasViewport.peek();
        const hasSameSize =
          current.width === width &&
          current.height === height &&
          current.pixelRatio === pixelRatio;

        if (hasSameSize) {
          return;
        }

        canvasViewport.value = { width, height, pixelRatio };
      }

      const observer = new ResizeObserver(updateCanvasViewport);
      observer.observe(targetCanvas);
      updateCanvasViewport();
      window.addEventListener("resize", updateCanvasViewport);

      return function cleanupCanvasResize() {
        observer.disconnect();
        window.removeEventListener("resize", updateCanvasViewport);
      };
    },
    [canvasRef, canvasViewport],
  );
}

/**
 * @returns {number}
 */
function getCanvasPixelRatio() {
  const pixelRatio = window.devicePixelRatio || 1;
  return clamp(pixelRatio, 1, 2);
}

/**
 * @param {import("@preact/signals").Signal<ConfirmDialog | null>} dialogSignal
 * @param {ConfirmDialog} dialog
 * @returns {void}
 */
function showConfirmation(dialogSignal, dialog) {
  dialogSignal.value = dialog;
}

/**
 * @param {number} activePointerCount
 * @returns {void}
 */
function setActivePointerCount(activePointerCount) {
  serviceWorkerUpdateState.activePointerCount = activePointerCount;
}

/**
 * @param {boolean} hasProjectContent
 * @returns {void}
 */
function setHasProjectContent(hasProjectContent) {
  serviceWorkerUpdateState.hasProjectContent = hasProjectContent;
}

/**
 * @param {boolean} isProcessing
 * @returns {void}
 */
function setProcessingState(isProcessing) {
  serviceWorkerUpdateState.isProcessing = isProcessing;
}

/**
 * @returns {boolean}
 */
function isAppIdle() {
  let result = true;

  if (serviceWorkerUpdateState.hasProjectContent) {
    result = false;
    return result;
  }

  if (serviceWorkerUpdateState.isProcessing) {
    result = false;
    return result;
  }

  if (serviceWorkerUpdateState.activePointerCount > 0) {
    result = false;
    return result;
  }

  return result;
}

/**
 * @param {DataTransfer | null} clipboardData
 * @returns {File | null}
 */
function getImageFileFromClipboard(clipboardData) {
  let result = null;

  if (clipboardData === null) {
    return result;
  }

  const items = clipboardData.items;
  for (let i = 0, total = items.length; i < total; i++) {
    const item = items[i];
    if (!isAllowedImageType(item.type)) {
      continue;
    }

    const file = item.getAsFile();
    if (file !== null) {
      result = file;
      break;
    }
  }

  return result;
}

/**
 * @param {FileList | null} files
 * @returns {File | null}
 */
function getImageFileFromList(files) {
  let result = null;

  if (files === null) {
    return result;
  }

  for (let i = 0, total = files.length; i < total; i++) {
    const file = files[i];
    if (!isAllowedImageType(file.type)) {
      continue;
    }

    result = file;
    break;
  }

  return result;
}

/**
 * @param {string} type
 * @returns {boolean}
 */
function isAllowedImageType(type) {
  let result = false;
  if (type === "") {
    result = true;
    return result;
  }

  if (type.startsWith("image/")) {
    result = true;
  }

  return result;
}

/**
 * @param {File} file
 * @returns {Promise<SourceDocument>}
 */
async function createSourceDocument(file) {
  const dataUrl = await readFileAsDataUrl(file);
  const image = await loadImage(dataUrl);
  return {
    image,
    dataUrl,
    name: file.name || "document-photo",
    width: image.naturalWidth,
    height: image.naturalHeight,
  };
}

/**
 * @param {File} file
 * @returns {Promise<string>}
 */
function readFileAsDataUrl(file) {
  return new Promise(function readFile(resolve, reject) {
    const reader = new FileReader();
    reader.onload = function handleLoad() {
      const result = typeof reader.result === "string" ? reader.result : "";
      resolve(result);
    };
    reader.onerror = function handleError() {
      reject(new Error("Could not read image"));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * @param {string} dataUrl
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(dataUrl) {
  return new Promise(function loadImageElement(resolve, reject) {
    const image = new Image();
    image.decoding = "async";
    image.onload = function handleLoad() {
      resolve(image);
    };
    image.onerror = function handleError() {
      reject(new Error("Could not load image"));
    };
    image.src = dataUrl;
  });
}

/**
 * @param {number} width
 * @param {number} height
 * @returns {CropCorners}
 */
function createInitialCorners(width, height) {
  const insetX = Math.round(width * CONFIG.cropInsetRatio);
  const insetY = Math.round(height * CONFIG.cropInsetRatio);
  return {
    topLeft: { x: insetX, y: insetY },
    topRight: { x: width - insetX, y: insetY },
    bottomRight: { x: width - insetX, y: height - insetY },
    bottomLeft: { x: insetX, y: height - insetY },
  };
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {CanvasViewport} viewport
 * @param {ViewMode} viewMode
 * @param {SourceDocument | null} source
 * @param {CropCorners | null} corners
 * @param {CornerId | null} activeCornerId
 * @param {DragPreview | null} dragPreview
 * @param {CroppedDocument | null} cropped
 * @param {RedactionStroke[]} redactionStrokes
 * @param {RedactionStroke | null} redactionDraft
 * @param {ProcessedDocument | null} processed
 * @returns {void}
 */
function drawDocumentCanvas(
  canvas,
  viewport,
  viewMode,
  source,
  corners,
  activeCornerId,
  dragPreview,
  cropped,
  redactionStrokes,
  redactionDraft,
  processed,
) {
  const shouldDrawProcessed = viewMode === "processed" && processed !== null;
  if (shouldDrawProcessed) {
    drawProcessedCanvas(canvas, viewport, processed);
    return;
  }

  const shouldDrawWatermark = viewMode === "watermark" && cropped !== null;
  if (shouldDrawWatermark) {
    drawWatermarkEditorCanvas(
      canvas,
      viewport,
      cropped,
      redactionStrokes,
      redactionDraft,
    );
    return;
  }

  drawEditorCanvas(
    canvas,
    viewport,
    source,
    corners,
    activeCornerId,
    dragPreview,
  );
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {CanvasViewport} viewport
 * @param {CroppedDocument} cropped
 * @param {RedactionStroke[]} redactionStrokes
 * @param {RedactionStroke | null} redactionDraft
 * @returns {void}
 */
function drawWatermarkEditorCanvas(
  canvas,
  viewport,
  cropped,
  redactionStrokes,
  redactionDraft,
) {
  const ctx = prepareCanvasContext(canvas, viewport);
  if (ctx === null) {
    return;
  }

  const placement = getContainedPlacement(cropped, viewport);
  ctx.save();
  ctx.clearRect(0, 0, viewport.width, viewport.height);
  ctx.fillStyle = "#1c1917";
  ctx.fillRect(0, 0, viewport.width, viewport.height);
  ctx.shadowColor = "rgba(0,0,0,0.28)";
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 8;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(placement.x, placement.y, placement.width, placement.height);
  ctx.shadowColor = "transparent";
  ctx.drawImage(
    cropped.canvas,
    placement.x,
    placement.y,
    placement.width,
    placement.height,
  );

  ctx.translate(placement.x, placement.y);
  ctx.scale(placement.scale, placement.scale);
  drawRedactionStrokes(ctx, redactionStrokes);
  drawRedactionDraft(ctx, redactionDraft);
  ctx.restore();
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {CanvasViewport} viewport
 * @param {SourceDocument | null} source
 * @param {CropCorners | null} corners
 * @param {CornerId | null} activeCornerId
 * @param {DragPreview | null} dragPreview
 * @returns {void}
 */
function drawEditorCanvas(
  canvas,
  viewport,
  source,
  corners,
  activeCornerId,
  dragPreview,
) {
  if (source === null) {
    drawEmptyEditorCanvas(canvas, viewport);
    return;
  }

  const ctx = prepareCanvasContext(canvas, viewport);
  if (ctx === null) {
    return;
  }

  const placement = getImagePlacement(source, viewport);
  ctx.save();
  ctx.clearRect(0, 0, viewport.width, viewport.height);
  ctx.fillStyle = "#1c1917";
  ctx.fillRect(0, 0, viewport.width, viewport.height);
  ctx.drawImage(
    source.image,
    placement.x,
    placement.y,
    placement.width,
    placement.height,
  );

  if (corners !== null) {
    drawCropOverlay(ctx, placement, corners, activeCornerId);
  }

  if (dragPreview !== null) {
    drawDragLens(ctx, placement, source, dragPreview, viewport);
  }

  ctx.restore();
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {CanvasViewport} viewport
 * @returns {void}
 */
function drawEmptyEditorCanvas(canvas, viewport) {
  const ctx = prepareCanvasContext(canvas, viewport);
  if (ctx === null) {
    return;
  }

  const width = viewport.width;
  const height = viewport.height;
  const shortestSide = Math.min(width, height);
  const scaledInset = Math.round(shortestSide * 0.08);
  const inset = Math.min(48, Math.max(16, scaledInset));

  ctx.save();
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = "#1c1917";
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = "rgba(255,255,255,0.14)";
  ctx.lineWidth = 2;
  ctx.setLineDash([18, 16]);
  const frameWidth = Math.max(1, width - inset * 2);
  const frameHeight = Math.max(1, height - inset * 2);
  ctx.strokeRect(inset, inset, frameWidth, frameHeight);
  ctx.setLineDash([]);
  ctx.fillStyle = "#e7e5e4";
  ctx.font = "600 20px Inter, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("Select an image", width / 2, height / 2);
  ctx.restore();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {ImagePlacement} placement
 * @param {CropCorners} corners
 * @param {CornerId | null} activeCornerId
 * @returns {void}
 */
function drawCropOverlay(ctx, placement, corners, activeCornerId) {
  const points = getCanvasCornerPoints(corners, placement);

  ctx.save();
  ctx.fillStyle = "rgba(28, 25, 23, 0.56)";
  ctx.beginPath();
  ctx.rect(placement.x, placement.y, placement.width, placement.height);
  appendPolygonPath(ctx, points);
  ctx.fill("evenodd");

  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 2;
  ctx.lineJoin = "round";
  ctx.beginPath();
  appendPolygonPath(ctx, points);
  ctx.closePath();
  ctx.stroke();

  ctx.strokeStyle = "#14b8a6";
  ctx.lineWidth = 3;
  ctx.beginPath();
  appendPolygonPath(ctx, points);
  ctx.closePath();
  ctx.stroke();

  for (let i = 0, total = cornerIds.length; i < total; i++) {
    const cornerId = cornerIds[i];
    const point = points[cornerId];
    const isActive = cornerId === activeCornerId;
    drawCornerHandle(ctx, point, isActive);
  }

  ctx.restore();
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {CanvasViewport} viewport
 * @param {ProcessedDocument} processed
 * @returns {void}
 */
function drawProcessedCanvas(canvas, viewport, processed) {
  const ctx = prepareCanvasContext(canvas, viewport);
  if (ctx === null) {
    return;
  }

  const processedSize = {
    width: processed.width,
    height: processed.height,
  };
  const placement = getContainedPlacement(processedSize, viewport);
  ctx.save();
  ctx.clearRect(0, 0, viewport.width, viewport.height);
  ctx.fillStyle = "#f5f5f4";
  ctx.fillRect(0, 0, viewport.width, viewport.height);
  ctx.shadowColor = "rgba(28,25,23,0.22)";
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 8;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(placement.x, placement.y, placement.width, placement.height);
  ctx.shadowColor = "transparent";
  ctx.drawImage(
    processed.canvas,
    placement.x,
    placement.y,
    placement.width,
    placement.height,
  );
  ctx.restore();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {Record<CornerId, Point>} points
 * @returns {void}
 */
function appendPolygonPath(ctx, points) {
  ctx.moveTo(points.topLeft.x, points.topLeft.y);
  ctx.lineTo(points.topRight.x, points.topRight.y);
  ctx.lineTo(points.bottomRight.x, points.bottomRight.y);
  ctx.lineTo(points.bottomLeft.x, points.bottomLeft.y);
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {Point} point
 * @param {boolean} isActive
 * @returns {void}
 */
function drawCornerHandle(ctx, point, isActive) {
  const radius = CONFIG.cornerHandleRadius;
  const fill = isActive ? "#f59e0b" : "#14b8a6";

  ctx.save();
  ctx.beginPath();
  ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.shadowColor = "rgba(0,0,0,0.32)";
  ctx.shadowBlur = 14;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.arc(point.x, point.y, radius - 5, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.restore();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {ImagePlacement} placement
 * @param {SourceDocument} source
 * @param {DragPreview} preview
 * @param {CanvasViewport} viewport
 * @returns {void}
 */
function drawDragLens(ctx, placement, source, preview, viewport) {
  const radius = getDragLensRadius(viewport);
  const imageRadius = radius - 4;
  const lensCenter = getDragLensCenter(preview.canvasPoint, viewport, radius);
  const focusPoint = sourceToCanvasPoint(preview.sourcePoint, placement);

  ctx.save();
  ctx.beginPath();
  ctx.arc(lensCenter.x, lensCenter.y, radius, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255, 255, 255, 0.96)";
  ctx.shadowColor = "rgba(0, 0, 0, 0.34)";
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 8;
  ctx.fill();

  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  drawDragLensImage(
    ctx,
    placement,
    source,
    focusPoint,
    lensCenter,
    imageRadius,
  );

  ctx.beginPath();
  ctx.arc(lensCenter.x, lensCenter.y, imageRadius, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(0, 0, 0, 0.22)";
  ctx.lineWidth = 1;
  ctx.stroke();

  drawDragLensCenterMark(ctx, lensCenter);
  ctx.restore();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {ImagePlacement} placement
 * @param {SourceDocument} source
 * @param {Point} focusPoint
 * @param {Point} lensCenter
 * @param {number} radius
 * @returns {void}
 */
function drawDragLensImage(
  ctx,
  placement,
  source,
  focusPoint,
  lensCenter,
  radius,
) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(lensCenter.x, lensCenter.y, radius, 0, Math.PI * 2);
  ctx.clip();
  ctx.fillStyle = "#1c1917";
  ctx.fillRect(
    lensCenter.x - radius,
    lensCenter.y - radius,
    radius * 2,
    radius * 2,
  );
  ctx.translate(lensCenter.x, lensCenter.y);
  ctx.scale(CONFIG.dragLensZoom, CONFIG.dragLensZoom);
  ctx.translate(-focusPoint.x, -focusPoint.y);
  ctx.drawImage(
    source.image,
    placement.x,
    placement.y,
    placement.width,
    placement.height,
  );
  ctx.restore();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {Point} lensCenter
 * @returns {void}
 */
function drawDragLensCenterMark(ctx, lensCenter) {
  ctx.save();
  ctx.lineCap = "round";
  ctx.strokeStyle = "rgba(28, 25, 23, 0.76)";
  ctx.lineWidth = 4;
  drawDragLensCrosshair(ctx, lensCenter, 15);
  ctx.strokeStyle = "rgba(255, 255, 255, 0.92)";
  ctx.lineWidth = 2;
  drawDragLensCrosshair(ctx, lensCenter, 15);

  ctx.beginPath();
  ctx.arc(lensCenter.x, lensCenter.y, 5, 0, Math.PI * 2);
  ctx.fillStyle = "#f59e0b";
  ctx.fill();
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {Point} center
 * @param {number} size
 * @returns {void}
 */
function drawDragLensCrosshair(ctx, center, size) {
  const gap = 7;

  ctx.beginPath();
  ctx.moveTo(center.x - size, center.y);
  ctx.lineTo(center.x - gap, center.y);
  ctx.moveTo(center.x + gap, center.y);
  ctx.lineTo(center.x + size, center.y);
  ctx.moveTo(center.x, center.y - size);
  ctx.lineTo(center.x, center.y - gap);
  ctx.moveTo(center.x, center.y + gap);
  ctx.lineTo(center.x, center.y + size);
  ctx.stroke();
}

/**
 * @param {CanvasViewport} viewport
 * @returns {number}
 */
function getDragLensRadius(viewport) {
  const shortestSide = Math.min(viewport.width, viewport.height);
  const scaledRadius = Math.round(shortestSide * 0.16);
  return Math.min(CONFIG.dragLensRadius, Math.max(46, scaledRadius));
}

/**
 * @param {Point} pointerPoint
 * @param {CanvasViewport} viewport
 * @param {number} radius
 * @returns {Point}
 */
function getDragLensCenter(pointerPoint, viewport, radius) {
  const padding = 12;
  const minX = radius + padding;
  const maxX = Math.max(minX, viewport.width - radius - padding);
  const minY = radius + padding;
  const maxY = Math.max(minY, viewport.height - radius - padding);
  const sideOffset = radius + CONFIG.dragLensGap;
  const leftX = pointerPoint.x - sideOffset;
  const rightX = pointerPoint.x + sideOffset;
  const prefersRightSide = pointerPoint.x < viewport.width / 2;
  let x = leftX;

  if (prefersRightSide) {
    x = rightX;
  }

  if (prefersRightSide && rightX > maxX && leftX >= minX) {
    x = leftX;
  }

  if (!prefersRightSide && leftX < minX && rightX <= maxX) {
    x = rightX;
  }

  const rawY = pointerPoint.y - radius * 0.4;
  return {
    x: clamp(x, minX, maxX),
    y: clamp(rawY, minY, maxY),
  };
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {number} width
 * @param {number} height
 * @returns {void}
 */
function setCanvasDimensions(canvas, width, height) {
  if (canvas.width !== width) {
    canvas.width = width;
  }

  if (canvas.height !== height) {
    canvas.height = height;
  }
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {CanvasViewport} viewport
 * @returns {CanvasRenderingContext2D | null}
 */
function prepareCanvasContext(canvas, viewport) {
  const rawPixelWidth = viewport.width * viewport.pixelRatio;
  const rawPixelHeight = viewport.height * viewport.pixelRatio;
  const pixelWidth = Math.max(1, Math.round(rawPixelWidth));
  const pixelHeight = Math.max(1, Math.round(rawPixelHeight));
  setCanvasDimensions(canvas, pixelWidth, pixelHeight);

  const ctx = canvas.getContext("2d");
  if (ctx === null) {
    return null;
  }

  ctx.setTransform(viewport.pixelRatio, 0, 0, viewport.pixelRatio, 0, 0);
  return ctx;
}

/**
 * @param {CanvasContentSize} contentSize
 * @param {CanvasViewport} viewport
 * @returns {ImagePlacement}
 */
function getContainedPlacement(contentSize, viewport) {
  const padding = getCanvasPadding(viewport);
  const contentWidth = Math.max(1, contentSize.width);
  const contentHeight = Math.max(1, contentSize.height);
  const availableWidth = Math.max(1, viewport.width - padding * 2);
  const availableHeight = Math.max(1, viewport.height - padding * 2);
  const widthScale = availableWidth / contentWidth;
  const heightScale = availableHeight / contentHeight;
  const scale = Math.min(widthScale, heightScale);
  const width = Math.max(1, contentWidth * scale);
  const height = Math.max(1, contentHeight * scale);
  const x = (viewport.width - width) / 2;
  const y = (viewport.height - height) / 2;

  return { x, y, width, height, scale };
}

/**
 * @param {SourceDocument} source
 * @param {CanvasViewport} viewport
 * @returns {ImagePlacement}
 */
function getImagePlacement(source, viewport) {
  return getContainedPlacement(source, viewport);
}

/**
 * @param {CanvasViewport} viewport
 * @returns {number}
 */
function getCanvasPadding(viewport) {
  const shortestSide = Math.min(viewport.width, viewport.height);
  const scaledPadding = Math.round(shortestSide * 0.035);
  return Math.min(CONFIG.canvasPadding, Math.max(10, scaledPadding));
}

/**
 * @param {CropCorners} corners
 * @param {ImagePlacement} placement
 * @returns {Record<CornerId, Point>}
 */
function getCanvasCornerPoints(corners, placement) {
  return {
    topLeft: sourceToCanvasPoint(corners.topLeft, placement),
    topRight: sourceToCanvasPoint(corners.topRight, placement),
    bottomRight: sourceToCanvasPoint(corners.bottomRight, placement),
    bottomLeft: sourceToCanvasPoint(corners.bottomLeft, placement),
  };
}

/**
 * @param {Point} point
 * @param {ImagePlacement} placement
 * @returns {Point}
 */
function sourceToCanvasPoint(point, placement) {
  return {
    x: placement.x + point.x * placement.scale,
    y: placement.y + point.y * placement.scale,
  };
}

/**
 * @param {Point} point
 * @param {ImagePlacement} placement
 * @returns {Point}
 */
function canvasToSourcePoint(point, placement) {
  return {
    x: (point.x - placement.x) / placement.scale,
    y: (point.y - placement.y) / placement.scale,
  };
}

/**
 * @param {PointerEvent} event
 * @param {HTMLCanvasElement} canvas
 * @returns {Point}
 */
function pointerToCanvasPoint(event, canvas) {
  const rect = canvas.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  return { x, y };
}

/**
 * @param {PointerEvent} event
 * @param {HTMLCanvasElement} canvas
 * @param {CanvasViewport} viewport
 * @param {CroppedDocument} cropped
 * @param {boolean} clampOutside
 * @returns {Point | null}
 */
function pointerToCroppedPoint(event, canvas, viewport, cropped, clampOutside) {
  let result = null;
  const canvasPoint = pointerToCanvasPoint(event, canvas);
  const placement = getContainedPlacement(cropped, viewport);
  const isInsideX =
    canvasPoint.x >= placement.x &&
    canvasPoint.x <= placement.x + placement.width;
  const isInsideY =
    canvasPoint.y >= placement.y &&
    canvasPoint.y <= placement.y + placement.height;

  if ((!isInsideX || !isInsideY) && !clampOutside) {
    return result;
  }

  const croppedPoint = canvasToSourcePoint(canvasPoint, placement);
  result = {
    x: clamp(croppedPoint.x, 0, cropped.width),
    y: clamp(croppedPoint.y, 0, cropped.height),
  };

  return result;
}

/**
 * @param {PointerEvent} event
 * @param {HTMLCanvasElement} canvas
 * @param {SourceDocument} source
 * @param {CropCorners} corners
 * @param {CanvasViewport} viewport
 * @returns {CornerId | null}
 */
function findHitCorner(event, canvas, source, corners, viewport) {
  let result = null;
  let bestDistance = CONFIG.cornerHitRadius;
  const placement = getImagePlacement(source, viewport);
  const points = getCanvasCornerPoints(corners, placement);
  const rect = canvas.getBoundingClientRect();

  for (let i = 0, total = cornerIds.length; i < total; i++) {
    const cornerId = cornerIds[i];
    const point = points[cornerId];
    const clientPoint = canvasPointToClientPoint(point, rect, viewport);
    const distance = Math.hypot(
      event.clientX - clientPoint.x,
      event.clientY - clientPoint.y,
    );

    if (distance > bestDistance) {
      continue;
    }

    bestDistance = distance;
    result = cornerId;
  }

  return result;
}

/**
 * @param {Point} point
 * @param {DOMRect} rect
 * @param {CanvasViewport} viewport
 * @returns {Point}
 */
function canvasPointToClientPoint(point, rect, viewport) {
  return {
    x: rect.left + (point.x / viewport.width) * rect.width,
    y: rect.top + (point.y / viewport.height) * rect.height,
  };
}

/**
 * @param {PointerEvent} event
 * @param {HTMLCanvasElement} canvas
 * @param {CanvasViewportSignal} canvasViewport
 * @param {SourceDocumentSignal} sourceDocument
 * @param {CropCornersSignal} cropCorners
 * @param {DragPreviewSignal} dragPreview
 * @param {CroppedDocumentSignal} croppedDocument
 * @param {RedactionStrokesSignal} redactionStrokes
 * @param {RedactionDraftSignal} redactionDraft
 * @param {ProcessedDocumentSignal} processedDocument
 * @param {CornerId} cornerId
 * @returns {void}
 */
function updateCornerFromPointer(
  event,
  canvas,
  canvasViewport,
  sourceDocument,
  cropCorners,
  dragPreview,
  croppedDocument,
  redactionStrokes,
  redactionDraft,
  processedDocument,
  cornerId,
) {
  const source = sourceDocument.peek();
  const corners = cropCorners.peek();
  if (source === null || corners === null) {
    dragPreview.value = null;
    return;
  }

  const canvasPoint = pointerToCanvasPoint(event, canvas);
  const viewport = canvasViewport.peek();
  const placement = getImagePlacement(source, viewport);
  const sourcePoint = canvasToSourcePoint(canvasPoint, placement);
  const clampedPoint = {
    x: clamp(sourcePoint.x, 0, source.width),
    y: clamp(sourcePoint.y, 0, source.height),
  };
  const preview = {
    cornerId,
    canvasPoint,
    sourcePoint: clampedPoint,
  };
  const nextCorners = cloneCorners(corners);
  nextCorners[cornerId] = clampedPoint;
  const hasUsableCrop = isUsableQuadrilateral(nextCorners);

  batch(function updateCornerDragPreview() {
    dragPreview.value = preview;
    if (hasUsableCrop) {
      cropCorners.value = nextCorners;
      croppedDocument.value = null;
      redactionStrokes.value = [];
      redactionDraft.value = null;
      clearProcessedDocument(processedDocument);
    }
  });
}

/**
 * @param {CropCorners} corners
 * @returns {CropCorners}
 */
function cloneCorners(corners) {
  return {
    topLeft: { ...corners.topLeft },
    topRight: { ...corners.topRight },
    bottomRight: { ...corners.bottomRight },
    bottomLeft: { ...corners.bottomLeft },
  };
}

/**
 * @param {CropCorners} corners
 * @returns {boolean}
 */
function isUsableQuadrilateral(corners) {
  let result = true;
  const area = Math.abs(getPolygonArea(corners));

  if (area < CONFIG.minPolygonArea) {
    result = false;
    return result;
  }

  const points = getOrderedCornerPoints(corners);
  let sign = 0;
  for (let i = 0, total = points.length; i < total; i++) {
    const previous = points[i];
    const current = points[(i + 1) % total];
    const next = points[(i + 2) % total];
    const cross = crossProduct(previous, current, next);
    if (Math.abs(cross) < 1) {
      result = false;
      return result;
    }

    const currentSign = Math.sign(cross);
    if (sign === 0) {
      sign = currentSign;
      continue;
    }

    if (sign !== currentSign) {
      result = false;
      return result;
    }
  }

  return result;
}

/**
 * @param {CropCorners} corners
 * @returns {Point[]}
 */
function getOrderedCornerPoints(corners) {
  return [
    corners.topLeft,
    corners.topRight,
    corners.bottomRight,
    corners.bottomLeft,
  ];
}

/**
 * @param {CropCorners} corners
 * @returns {number}
 */
function getPolygonArea(corners) {
  let area = 0;
  const points = getOrderedCornerPoints(corners);
  for (let i = 0, total = points.length; i < total; i++) {
    const current = points[i];
    const next = points[(i + 1) % total];
    area += current.x * next.y - next.x * current.y;
  }

  return area / 2;
}

/**
 * @param {Point} first
 * @param {Point} second
 * @param {Point} third
 * @returns {number}
 */
function crossProduct(first, second, third) {
  const abX = second.x - first.x;
  const abY = second.y - first.y;
  const bcX = third.x - second.x;
  const bcY = third.y - second.y;
  return abX * bcY - abY * bcX;
}

/**
 * @param {SourceDocument} source
 * @param {CropCorners} corners
 * @param {OutputSettings} settings
 * @returns {CroppedDocument}
 */
function createCroppedDocument(source, corners, settings) {
  const outputSize = calculateOutputSize(corners, settings.maxDimension);
  const canvas = createWarpedCanvas(source, corners, outputSize);
  applyPostCropOperations(canvas, settings.operations);
  const baseName = getSafeBaseName(source.name);

  return {
    canvas,
    name: baseName,
    width: outputSize.width,
    height: outputSize.height,
  };
}

/**
 * @param {CroppedDocument} cropped
 * @param {RedactionStroke[]} redactionStrokes
 * @param {string | null} watermarkText
 * @returns {Promise<ProcessedDocument>}
 */
async function createProcessedDocument(
  cropped,
  redactionStrokes,
  watermarkText,
) {
  const redactedCanvas = createRedactedCanvas(cropped.canvas, redactionStrokes);
  let canvas = redactedCanvas;
  let name = `${cropped.name}-cropped.png`;
  if (redactionStrokes.length > 0) {
    name = `${cropped.name}-redacted.png`;
  }
  if (watermarkText !== null) {
    canvas = await applyWatermarkToCanvas({ imageCanvas: redactedCanvas, watermarkText });
    const safeWatermark = getSafeBaseName(normalizeWatermarkText(watermarkText));
    name = `${cropped.name}-${safeWatermark}-watermarked.png`;
  }
  const blob = await canvasToBlob(canvas, "image/png", 0.96);
  const url = URL.createObjectURL(blob);

  return {
    canvas,
    blob,
    url,
    name,
    width: canvas.width,
    height: canvas.height,
  };
}

/**
 * @param {SourceDocument} source
 * @param {CropCorners} corners
 * @param {OutputSize} outputSize
 * @returns {HTMLCanvasElement}
 */
function createWarpedCanvas(source, corners, outputSize) {
  const sourceCanvas = document.createElement("canvas");
  sourceCanvas.width = source.width;
  sourceCanvas.height = source.height;

  const sourceCtx = sourceCanvas.getContext("2d", {
    willReadFrequently: true,
  });
  if (sourceCtx === null) {
    throw new Error("Could not read source canvas");
  }

  sourceCtx.drawImage(source.image, 0, 0, source.width, source.height);
  const sourceImageData = sourceCtx.getImageData(
    0,
    0,
    source.width,
    source.height,
  );
  const outputCanvas = document.createElement("canvas");
  outputCanvas.width = outputSize.width;
  outputCanvas.height = outputSize.height;

  const outputCtx = outputCanvas.getContext("2d");
  if (outputCtx === null) {
    throw new Error("Could not create output canvas");
  }

  const transform = getDestinationToSourceTransform(corners, outputSize);
  const outputImageData = outputCtx.createImageData(
    outputSize.width,
    outputSize.height,
  );
  writeWarpedPixels(sourceImageData, outputImageData, transform);
  outputCtx.putImageData(outputImageData, 0, 0);
  return outputCanvas;
}

/**
 * @param {ImageData} sourceImageData
 * @param {ImageData} outputImageData
 * @param {number[]} transform
 * @returns {void}
 */
function writeWarpedPixels(sourceImageData, outputImageData, transform) {
  const sourceWidth = sourceImageData.width;
  const sourceHeight = sourceImageData.height;
  const outputWidth = outputImageData.width;
  const outputHeight = outputImageData.height;
  const sourceData = sourceImageData.data;
  const outputData = outputImageData.data;

  for (let y = 0; y < outputHeight; y++) {
    for (let x = 0; x < outputWidth; x++) {
      const outputIndex =
        (y * outputWidth + x) * CONFIG.imageDataChannelCount;
      const sampleX = x + CONFIG.warpPixelCenterOffset;
      const sampleY = y + CONFIG.warpPixelCenterOffset;
      const point = transformPoint(transform, sampleX, sampleY);
      sampleBilinear(
        sourceData,
        sourceWidth,
        sourceHeight,
        point.x,
        point.y,
        outputData,
        outputIndex,
      );
    }
  }
}

/**
 * @param {Uint8ClampedArray} sourceData
 * @param {number} sourceWidth
 * @param {number} sourceHeight
 * @param {number} x
 * @param {number} y
 * @param {Uint8ClampedArray} outputData
 * @param {number} outputIndex
 * @returns {void}
 */
function sampleBilinear(
  sourceData,
  sourceWidth,
  sourceHeight,
  x,
  y,
  outputData,
  outputIndex,
) {
  const minX = 0;
  const minY = 0;
  const maxX = sourceWidth - 1;
  const maxY = sourceHeight - 1;
  if (x < minX || y < minY || x > maxX || y > maxY) {
    for (let channel = 0; channel < CONFIG.imageDataChannelCount; channel++) {
      outputData[outputIndex + channel] = CONFIG.warpBackgroundValue;
    }

    return;
  }

  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(x0 + CONFIG.bilinearNeighborOffset, maxX);
  const y1 = Math.min(y0 + CONFIG.bilinearNeighborOffset, maxY);
  const tx = x - x0;
  const ty = y - y0;
  const topLeftIndex =
    (y0 * sourceWidth + x0) * CONFIG.imageDataChannelCount;
  const topRightIndex =
    (y0 * sourceWidth + x1) * CONFIG.imageDataChannelCount;
  const bottomLeftIndex =
    (y1 * sourceWidth + x0) * CONFIG.imageDataChannelCount;
  const bottomRightIndex =
    (y1 * sourceWidth + x1) * CONFIG.imageDataChannelCount;

  for (let channel = 0; channel < CONFIG.imageDataChannelCount; channel++) {
    const topLeft = sourceData[topLeftIndex + channel];
    const topRight = sourceData[topRightIndex + channel];
    const bottomLeft = sourceData[bottomLeftIndex + channel];
    const bottomRight = sourceData[bottomRightIndex + channel];
    const top = topLeft + (topRight - topLeft) * tx;
    const bottom = bottomLeft + (bottomRight - bottomLeft) * tx;
    outputData[outputIndex + channel] = top + (bottom - top) * ty;
  }
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {unknown[]} operations
 * @returns {void}
 */
function applyPostCropOperations(canvas, operations) {
  if (operations.length === 0) {
    return;
  }

  const ctx = canvas.getContext("2d");
  if (ctx === null) {
    return;
  }

  for (let i = 0, total = operations.length; i < total; i++) {
    const operation = operations[i];
    void operation;
    void ctx;
  }
}

/**
 * @param {CropCorners} corners
 * @param {number} maxDimension
 * @returns {OutputSize}
 */
function calculateOutputSize(corners, maxDimension) {
  const topWidth = distance(corners.topLeft, corners.topRight);
  const bottomWidth = distance(corners.bottomLeft, corners.bottomRight);
  const leftHeight = distance(corners.topLeft, corners.bottomLeft);
  const rightHeight = distance(corners.topRight, corners.bottomRight);
  let width = Math.max(topWidth, bottomWidth);
  let height = Math.max(leftHeight, rightHeight);
  const largestDimension = Math.max(width, height);

  if (largestDimension > maxDimension) {
    const scale = maxDimension / largestDimension;
    width *= scale;
    height *= scale;
  }

  return {
    width: Math.max(1, Math.round(width)),
    height: Math.max(1, Math.round(height)),
  };
}

/**
 * @param {Point} first
 * @param {Point} second
 * @returns {number}
 */
function distance(first, second) {
  const dx = second.x - first.x;
  const dy = second.y - first.y;
  return Math.hypot(dx, dy);
}

/**
 * @param {CropCorners} corners
 * @param {OutputSize} outputSize
 * @returns {number[]}
 */
function getDestinationToSourceTransform(corners, outputSize) {
  const maxX = Math.max(1, outputSize.width - 1);
  const maxY = Math.max(1, outputSize.height - 1);
  const destination = [
    { x: 0, y: 0 },
    { x: maxX, y: 0 },
    { x: maxX, y: maxY },
    { x: 0, y: maxY },
  ];
  const source = getOrderedCornerPoints(corners);
  const matrix = [];
  const vector = [];

  for (let i = 0, total = destination.length; i < total; i++) {
    const destinationPoint = destination[i];
    const sourcePoint = source[i];
    const u = destinationPoint.x;
    const v = destinationPoint.y;
    const x = sourcePoint.x;
    const y = sourcePoint.y;
    matrix.push([u, v, 1, 0, 0, 0, -x * u, -x * v]);
    vector.push(x);
    matrix.push([0, 0, 0, u, v, 1, -y * u, -y * v]);
    vector.push(y);
  }

  return solveLinearSystem(matrix, vector);
}

/**
 * @param {number[]} transform
 * @param {number} x
 * @param {number} y
 * @returns {Point}
 */
function transformPoint(transform, x, y) {
  const denominator = transform[6] * x + transform[7] * y + 1;
  if (Math.abs(denominator) < 1e-12) {
    return { x: -1, y: -1 };
  }

  return {
    x: (transform[0] * x + transform[1] * y + transform[2]) / denominator,
    y: (transform[3] * x + transform[4] * y + transform[5]) / denominator,
  };
}

/**
 * @param {number[][]} matrix
 * @param {number[]} vector
 * @returns {number[]}
 */
function solveLinearSystem(matrix, vector) {
  const size = vector.length;
  const augmented = [];

  for (let rowIndex = 0; rowIndex < size; rowIndex++) {
    const row = matrix[rowIndex].slice();
    row.push(vector[rowIndex]);
    augmented.push(row);
  }

  for (let column = 0; column < size; column++) {
    const pivotRow = findPivotRow(augmented, column, size);
    const pivotValue = augmented[pivotRow][column];
    if (Math.abs(pivotValue) < 1e-12) {
      throw new Error("Crop shape is too narrow");
    }

    if (pivotRow !== column) {
      const temp = /** @type {number[]} */ (augmented[column]);
      augmented[column] = augmented[pivotRow];
      augmented[pivotRow] = temp;
    }

    normalizePivotRow(augmented[column], column, size);
    eliminateColumn(augmented, column, size);
  }

  const result = [];
  for (let i = 0; i < size; i++) {
    result.push(augmented[i][size]);
  }

  return result;
}

/**
 * @param {number[][]} augmented
 * @param {number} column
 * @param {number} size
 * @returns {number}
 */
function findPivotRow(augmented, column, size) {
  let pivotRow = column;
  let pivotSize = Math.abs(augmented[column][column]);

  for (let rowIndex = column + 1; rowIndex < size; rowIndex++) {
    const value = Math.abs(augmented[rowIndex][column]);
    if (value <= pivotSize) {
      continue;
    }

    pivotRow = rowIndex;
    pivotSize = value;
  }

  return pivotRow;
}

/**
 * @param {number[]} row
 * @param {number} column
 * @param {number} size
 * @returns {void}
 */
function normalizePivotRow(row, column, size) {
  const pivotValue = row[column];
  for (let i = column; i <= size; i++) {
    row[i] /= pivotValue;
  }
}

/**
 * @param {number[][]} augmented
 * @param {number} column
 * @param {number} size
 * @returns {void}
 */
function eliminateColumn(augmented, column, size) {
  for (let rowIndex = 0; rowIndex < size; rowIndex++) {
    if (rowIndex === column) {
      continue;
    }

    const factor = augmented[rowIndex][column];
    for (let i = column; i <= size; i++) {
      augmented[rowIndex][i] -= factor * augmented[column][i];
    }
  }
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {"image/png"} format
 * @param {number} quality
 * @returns {Promise<Blob>}
 */
function canvasToBlob(canvas, format, quality) {
  return new Promise(function createBlob(resolve, reject) {
    canvas.toBlob(
      function handleBlob(blob) {
        if (blob === null) {
          reject(new Error("Could not create scan"));
          return;
        }

        resolve(blob);
      },
      format,
      quality,
    );
  });
}

/**
 * @param {ProcessedDocumentSignal} signal
 * @param {ProcessedDocument} processed
 * @returns {void}
 */
function replaceProcessedDocument(signal, processed) {
  clearProcessedDocument(signal);
  signal.value = processed;
}

/**
 * @param {ProcessedDocumentSignal} signal
 * @returns {void}
 */
function clearProcessedDocument(signal) {
  const current = signal.peek();
  if (current === null) {
    return;
  }

  URL.revokeObjectURL(current.url);
  signal.value = null;
}

/**
 * @param {Blob} blob
 * @param {string} name
 * @returns {void}
 */
function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.requestAnimationFrame(function revokeDownloadUrl() {
    URL.revokeObjectURL(url);
  });
}

/**
 * @returns {Promise<void>}
 */
function waitForNextFrame() {
  return new Promise(function wait(resolve) {
    window.requestAnimationFrame(function handleFrame() {
      resolve();
    });
  });
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * @param {unknown} error
 * @returns {string}
 */
function getErrorMessage(error) {
  let message = String(error);
  if (error instanceof Error) {
    message = error.message;
  }

  return message;
}

/**
 * @param {string} name
 * @returns {string}
 */
function getSafeBaseName(name) {
  const withoutExtension = name.replace(/\.[^.]+$/, "");
  const safeName = withoutExtension.replace(/[^a-z0-9_-]+/gi, "-");
  let result = safeName.replace(/^-+|-+$/g, "").toLowerCase();
  if (result.length === 0) {
    result = "doc-proc";
  }

  return result;
}

/**
 * @param {string} name
 * @returns {string}
 */
function createDefaultWatermarkCode(name) {
  const baseName = getSafeBaseName(name);
  let result = baseName.replace(/-/g, " ").toUpperCase();
  if (result.length === 0) {
    result = "CONTROLLED COPY";
  }

  return result;
}

/**
 * @param {ViewMode} viewMode
 * @returns {string}
 */
function getViewModeLabel(viewMode) {
  let result = "Cropping";
  if (viewMode === "watermark") {
    result = "Watermarking";
  }

  if (viewMode === "processed") {
    result = "Processed";
  }

  return result;
}

/**
 * @param {SourceDocument | null} source
 * @returns {string}
 */
function getSourceDetailValue(source) {
  let result = "No image";
  if (source !== null) {
    result = `${source.name} - ${source.width} x ${source.height}`;
  }

  return result;
}

/**
 * @param {OutputSize | null} outputSize
 * @returns {string}
 */
function getOutputSizeDetailValue(outputSize) {
  let result = "No output";
  if (outputSize !== null) {
    result = `${outputSize.width} x ${outputSize.height}`;
  }

  return result;
}

/**
 * @param {CroppedDocument | null} cropped
 * @returns {string}
 */
function getCroppedDetailValue(cropped) {
  let result = "None";
  if (cropped !== null) {
    result = `${cropped.width} x ${cropped.height}`;
  }

  return result;
}

/**
 * @param {ProcessedDocument | null} processed
 * @returns {string}
 */
function getProcessedDetailValue(processed) {
  let result = "None";
  if (processed !== null) {
    result = `${processed.name} - ${processed.width} x ${processed.height}`;
  }

  return result;
}

/**
 * @param {CanvasViewport} viewport
 * @returns {string}
 */
function getViewportDetailValue(viewport) {
  const pixelRatio = Math.round(viewport.pixelRatio * 100) / 100;
  return `${viewport.width} x ${viewport.height} @ ${pixelRatio}x`;
}

/**
 * @param {CropCorners | null} corners
 * @returns {import("preact").ComponentChildren[]}
 */
function getCornerDetailRows(corners) {
  if (corners === null) {
    return [
      html`<div><dt>Crop</dt><dd>No crop selected</dd></div>`,
    ];
  }

  const rows = [];
  for (let i = 0, total = cornerIds.length; i < total; i++) {
    const cornerId = cornerIds[i];
    const corner = corners[cornerId];
    const value = `${Math.round(corner.x)}, ${Math.round(corner.y)}`;
    rows.push(html`
      <div key=${cornerId} class="detail-row">
        <dt>${cornerLabels[cornerId]}</dt>
        <dd>${value}</dd>
      </div>
    `);
  }

  return rows;
}

/**
 * @param {ViewMode} viewMode
 * @param {SourceDocument | null} source
 * @param {CroppedDocument | null} cropped
 * @param {ProcessedDocument | null} processed
 * @param {OutputSize | null} plannedOutputSize
 * @param {OutputSize | null} watermarkOutputSize
 * @returns {string}
 */
function getCanvasMeta(
  viewMode,
  source,
  cropped,
  processed,
  plannedOutputSize,
  watermarkOutputSize,
) {
  let result = "Select image";
  if (source !== null) {
    result = `Crop ${source.width} x ${source.height}`;
  }

  const hasProcessedView = viewMode === "processed" && processed !== null;
  if (hasProcessedView) {
    result = `Processed ${processed.width} x ${processed.height}`;
    return result;
  }

  const hasWatermarkView = viewMode === "watermark" && cropped !== null;
  if (hasWatermarkView) {
    result = `Watermark ${cropped.width} x ${cropped.height}`;
    if (watermarkOutputSize !== null) {
      result += ` -> ${watermarkOutputSize.width} x ${watermarkOutputSize.height}`;
    }

    return result;
  }

  if (plannedOutputSize !== null) {
    result += ` -> ${plannedOutputSize.width} x ${plannedOutputSize.height}`;
  }

  return result;
}

function syncStandaloneClass() {
  const standalone = isStandalonePwa();
  document.documentElement.classList.toggle("is-standalone", standalone);
}

function syncViewportHeight() {
  const hasKeyboardSafeFocus = isKeyboardSafeControlFocused();
  const viewportHeight = getAppViewportHeight(hasKeyboardSafeFocus);
  const keyboardInset = getKeyboardInset(hasKeyboardSafeFocus);

  document.documentElement.style.setProperty(
    "--app-viewport-height",
    viewportHeight,
  );
  document.documentElement.style.setProperty(
    "--app-keyboard-inset",
    `${keyboardInset}px`,
  );
}

/**
 * @param {boolean} hasKeyboardSafeFocus
 * @returns {string}
 */
function getAppViewportHeight(hasKeyboardSafeFocus) {
  if (isStandalonePwa()) {
    return "100vh";
  }

  const compactHeight = getCompactViewportHeight();
  let height = compactHeight;
  if (
    hasKeyboardSafeFocus &&
    isPositiveNumber(appViewportState.stableHeight)
  ) {
    height = appViewportState.stableHeight;
  }

  if (!hasKeyboardSafeFocus && isPositiveNumber(compactHeight)) {
    appViewportState.stableHeight = compactHeight;
  }

  let result = "100vh";
  if (isPositiveNumber(height)) {
    result = `${Math.round(height)}px`;
  }

  return result;
}

/**
 * @returns {number}
 */
function getCompactViewportHeight() {
  const documentHeight = document.documentElement.clientHeight;
  const heightOptions = [window.innerHeight, documentHeight];
  const viewport = window.visualViewport;

  if (viewport) {
    heightOptions.push(viewport.height);
  }

  let result = window.innerHeight;
  for (let i = 0, total = heightOptions.length; i < total; i++) {
    const height = heightOptions[i];
    if (!isPositiveNumber(height)) {
      continue;
    }

    if (height < result) {
      result = height;
    }
  }

  return result;
}

/**
 * @param {boolean} hasKeyboardSafeFocus
 * @returns {number}
 */
function getKeyboardInset(hasKeyboardSafeFocus) {
  let result = 0;
  if (!hasKeyboardSafeFocus) {
    return result;
  }

  const viewport = window.visualViewport;
  if (!viewport) {
    return result;
  }

  const documentHeight = document.documentElement.clientHeight;
  const layoutHeight = Math.max(window.innerHeight, documentHeight);
  const viewportBottom = viewport.offsetTop + viewport.height;
  const inset = layoutHeight - viewportBottom;

  if (isPositiveNumber(inset)) {
    result = Math.round(inset);
  }

  return result;
}

/**
 * @param {HTMLElement} element
 * @returns {void}
 */
function requestKeyboardSafeScroll(element) {
  scrollKeyboardSafeElementIntoView(element);

  window.requestAnimationFrame(function scrollAfterFocusFrame() {
    syncViewportHeight();
    scrollKeyboardSafeElementIntoView(element);
  });
}

function scrollActiveKeyboardSafeControlIntoView() {
  if (!isKeyboardSafeControlFocused()) {
    return;
  }

  const activeElement = document.activeElement;
  if (!(activeElement instanceof HTMLElement)) {
    return;
  }

  scrollKeyboardSafeElementIntoView(activeElement);
}

/**
 * @returns {boolean}
 */
function isKeyboardSafeControlFocused() {
  let result = false;
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement) {
    result = activeElement.classList.contains("keyboard-safe-control");
  }

  return result;
}

/**
 * @param {HTMLElement} element
 * @returns {void}
 */
function scrollKeyboardSafeElementIntoView(element) {
  const appShell = document.getElementById(APP_SHELL_ID);
  if (!(appShell instanceof HTMLElement)) {
    element.scrollIntoView({ block: "nearest", inline: "nearest" });
    return;
  }

  const rect = element.getBoundingClientRect();
  const safeViewport = getKeyboardSafeViewport();
  const scrollDelta = getKeyboardSafeScrollDelta(rect, safeViewport);
  if (scrollDelta === 0) {
    return;
  }

  appShell.scrollTop += scrollDelta;
}

/**
 * @returns {KeyboardSafeViewport}
 */
function getKeyboardSafeViewport() {
  const viewport = window.visualViewport;
  let top = 0;
  let height = window.innerHeight;

  if (viewport) {
    top = viewport.offsetTop;
    height = viewport.height;
  }

  return {
    top: top + CONFIG.keyboardSafeGap,
    bottom: top + height - CONFIG.keyboardSafeGap,
  };
}

/**
 * @param {DOMRect} rect
 * @param {KeyboardSafeViewport} safeViewport
 * @returns {number}
 */
function getKeyboardSafeScrollDelta(rect, safeViewport) {
  let result = 0;
  if (rect.bottom > safeViewport.bottom) {
    result = rect.bottom - safeViewport.bottom;
  }

  if (rect.top < safeViewport.top) {
    result = rect.top - safeViewport.top;
  }

  return Math.round(result);
}

/**
 * @param {number} value
 * @returns {boolean}
 */
function isPositiveNumber(value) {
  return Number.isFinite(value) && value > 0;
}

/**
 * @returns {boolean}
 */
function isStandalonePwa() {
  let result = false;

  if (window.matchMedia) {
    const displayMode = window.matchMedia("(display-mode: standalone)");
    result = displayMode.matches;
  }

  const nav = /** @type {Navigator & { standalone?: boolean }} */ (navigator);
  if (nav.standalone) {
    result = true;
  }

  return result;
}

function setupStandaloneClassListener() {
  syncStandaloneClass();

  if (!window.matchMedia) {
    return;
  }

  function handleDisplayModeChange() {
    syncStandaloneClass();
    syncViewportHeight();
  }

  const displayMode = window.matchMedia("(display-mode: standalone)");
  displayMode.addEventListener("change", handleDisplayModeChange);
}

function setupViewportHeightListener() {
  function handleViewportGeometryChange() {
    syncViewportHeight();
    scrollActiveKeyboardSafeControlIntoView();
  }

  syncViewportHeight();
  window.requestAnimationFrame(handleViewportGeometryChange);
  window.addEventListener("resize", handleViewportGeometryChange);
  window.addEventListener("load", handleViewportGeometryChange);
  window.addEventListener("orientationchange", handleViewportGeometryChange);

  if (!window.visualViewport) {
    return;
  }

  window.visualViewport.addEventListener("resize", handleViewportGeometryChange);
  window.visualViewport.addEventListener("scroll", handleViewportGeometryChange);
}

const rootElement = document.getElementById("root");
if (!(rootElement instanceof HTMLElement)) {
  throw new Error("Root element not found");
}

setupStandaloneClassListener();
setupViewportHeightListener();
render(html`<${App} />`, rootElement);
void setupServiceWorkerReloadWhenIdle({ isIdle: isAppIdle });
